import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Award, CheckCircle, Star, Zap } from 'lucide-react';

const AwardsSection = () => {
  const awards = [
    { icon: Trophy, title: 'Rookie of the Year', org: 'Avalara FY2024', color: '#F59E0B', glow: true },
    { icon: Award, title: 'MVP x 3 Quarters', org: 'Zoom', color: '#8B5CF6', glow: true },
    { icon: Trophy, title: 'Highest Indirect Deal Volume', org: 'Zoom', color: '#06B6D4', glow: false },
    { icon: Award, title: 'Top Salesperson x 4 Quarters', org: 'Searce', color: '#A78BFA', glow: false },
    { icon: Trophy, title: 'Highest ACV Contribution', org: 'Salesforce India FY2020', color: '#22D3EE', glow: false },
    { icon: CheckCircle, title: 'Google Certified', org: 'Google Apps & Google Cloud Platform Sales', color: '#8B5CF6', glow: false }
  ];

  return (
    <section className="py-24 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-[#2A1F5E]/20 via-transparent to-[#2A1F5E]/20" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-block mb-4">
            <span className="px-4 py-2 bg-white/10 backdrop-blur-md border border-[#F59E0B]/30 rounded-full text-[#F59E0B] text-xs font-mono font-semibold tracking-wider">
              RECOGNITION
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white" data-testid="awards-heading">
            Awards & Certifications
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-[#8B5CF6]/10 via-[#06B6D4]/10 to-[#8B5CF6]/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 mb-12 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#8B5CF6]/5 via-transparent to-[#06B6D4]/5 animate-pulse" />
          
          <div className="relative z-10">
            <div className="flex justify-center mb-4">
              <div className="p-4 rounded-xl bg-white/5" style={{ boxShadow: '0 0 30px rgba(245, 158, 11, 0.4)' }}>
                <Zap className="text-[#F59E0B]" size={40} style={{ filter: 'drop-shadow(0 0 10px #F59E0B)' }} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-[#F59E0B] mb-3">Consistent Top Performer</h3>
            <p className="text-white/80 text-base font-mono">110%+ Average Quota Achievement Across 9 Years</p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {awards.map((award, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 text-center relative overflow-hidden group"
              data-testid={`award-card-${index}`}
            >
              <div 
                className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500"
                style={{ background: `radial-gradient(circle at center, ${award.color} 0%, transparent 70%)` }}
              />
              
              {award.glow && (
                <div className="absolute top-2 right-2">
                  <div className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" />
                </div>
              )}
              
              <div className="relative z-10">
                <div 
                  className="inline-flex p-5 rounded-xl bg-white/5 mb-5"
                  style={{ boxShadow: `0 0 25px ${award.color}40` }}
                >
                  <award.icon 
                    size={32} 
                    style={{ color: award.color, filter: `drop-shadow(0 0 8px ${award.color})` }} 
                  />
                </div>
                <h3 className="text-base font-bold mb-3 text-white leading-snug">{award.title}</h3>
                <p className="text-sm text-white/60">{award.org}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AwardsSection;